import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCommentSms, faEnvelope, faMobileNotch, faSquareArrowUpRight,
} from '@fortawesome/pro-solid-svg-icons';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import AppTypes from '@avtechsoftware/ra-javascript-utilities/src/types/AppTypes';
import ExtremeTemperatureFaultChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/ExtremeTemperatureFaultChannel/ExtremeTemperatureFaultChannel';
import BooleanChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BooleanChannel/BooleanChannel';
import { selectByUuidOrMacAddress } from '../../../../redux/slices/deviceList/deviceListSlice';
import {
  formatDynamicDateDisplay,
  formatTimeDifferenceToDisplayDuration,
  formatTimestampToFullDate,
} from '../../../../utility/DateUtility';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';

/* istanbul ignore next */
const HistoryListItem = ({
  index,
  alertEvent,
}) => {
  const channelKeyObject = new ChannelKey(alertEvent.channelKey);

  const { temperatureScale, airSpeedScale } = useSelector(selectSessionInformation);
  const device = useSelector((state) => selectByUuidOrMacAddress(
    state, channelKeyObject.macAddress,
  ));

  const backgroundColor = index % 2 !== 0 ? '#EFEFEF' : '#FFFFFF';

  const channel = ChannelService.getChannelGroupChannelByAppType(channelKeyObject.channelGroupType,
    channelKeyObject.channelIndex, channelKeyObject.channelType, AppTypes.CLOUD);
  const isBooleanChannel = channel instanceof ExtremeTemperatureFaultChannel
    || channel instanceof BooleanChannel;

  const channelPath = `${device ? device.name : 'Unknown Device'} ${alertEvent.channelGroupName || alertEvent.channelGroupSourceName} \u00BB ${channel ? channel.displayName : 'Unknown Channel'}`;

  const triggerTime = formatTimestampToFullDate(alertEvent.triggerTimestamp,
    alertEvent.timezone);
  const clearTime = formatDynamicDateDisplay(
    alertEvent.clearTimestamp, alertEvent.triggerTimestamp,
    alertEvent.timezone,
  );
  const duration = formatTimeDifferenceToDisplayDuration(
    alertEvent.triggerTimestamp, alertEvent.clearTimestamp,
  );

  const triggeredValue = channel.getDisplayValueWithUnits(alertEvent.triggerValue,
    { scale: temperatureScale, customUnits: alertEvent.customUnit, airSpeedScale });
  const thresholdValue = channel.getDisplayValueWithUnits(alertEvent.startThreshold,
    { scale: temperatureScale, customUnits: alertEvent.customUnit, airSpeedScale });

  const clearValue = alertEvent.clearValue !== null
    ? channel.getDisplayValueWithUnits(alertEvent.clearValue,
      { scale: temperatureScale, customUnits: alertEvent.customUnit, airSpeedScale })
    : 'Alert Edited';
  const clearThreshold = channel.getDisplayValueWithUnits(alertEvent.clearThreshold,
    { scale: temperatureScale, customUnits: alertEvent.customUnit, airSpeedScale });

  return (
    <div style={{ backgroundColor }} className="py-2">
      <div className="row">
        <div className="col ml-3">
          <span className="text-muted font-weight-bold">
            {triggerTime}
            {' '}
            -
            {' '}
            {' '}
            {clearTime}
            {' '}
            (
            {duration}
            )
          </span>
        </div>
      </div>
      <div className="row">
        <div className="col ml-5">
          <span>
            {' '}
            {channelPath}
          </span>
        </div>
      </div>
      <div className="row">
        <div className="col ml-5">
          <span>
            <strong>
              Trigger:
            </strong>
            {' '}
            <span className="text-danger font-weight-bold">
              {triggeredValue}
            </span>
            {!isBooleanChannel && (
              <>
                {' '}
                {alertEvent.startOperator}
                {' '}
                {thresholdValue}
              </>
            )}
          </span>
        </div>
      </div>
      <div className="row">
        <div className="col ml-5">
          <span>
            <strong>
              Clear:
            </strong>
            {' '}
            <span className="text-success font-weight-bold">
              {clearValue}
            </span>
            {!isBooleanChannel && (
              <>
                {' '}
                {alertEvent.clearOperator}
                {' '}
                {clearThreshold}
              </>
            )}
          </span>
        </div>
      </div>
      <div className="row">
        <div className="col ml-5">
          {alertEvent.emailActionCount > 0 && (
            <span className="mx-2 text-muted">
              <FontAwesomeIcon title="email" icon={faEnvelope} className="your-className" />
              {' '}
              {alertEvent.emailActionCount}
            </span>
          )}
          {alertEvent.postActionCount > 0 && (
            <span className="mx-2 text-muted">
              <FontAwesomeIcon title="http-post" icon={faSquareArrowUpRight} className="your-className" />
              {' '}
              {alertEvent.postActionCount}
            </span>
          )}
          {alertEvent.realSmsActionCount > 0 && (
            <span className="mx-2 text-muted">
              <FontAwesomeIcon title="sms" icon={faCommentSms} className="your-className" />
              {' '}
              {alertEvent.realSmsActionCount}
            </span>
          )}
          {alertEvent.pushNotificationActionCount > 0 && (
            <span className="mx-2 text-muted">
              <FontAwesomeIcon title="push notification" icon={faMobileNotch} className="your-className" />
              {' '}
              {alertEvent.pushNotificationActionCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

HistoryListItem.propTypes = {
  index: PropTypes.number.isRequired,
  alertEvent: PropTypes.shape({
    alertId: PropTypes.number.isRequired,
    channelGroupName: PropTypes.string,
    channelGroupSourceName: PropTypes.string.isRequired,
    channelKey: PropTypes.string.isRequired,
    clearOperator: PropTypes.string.isRequired,
    clearThreshold: PropTypes.number.isRequired,
    clearTimestamp: PropTypes.string.isRequired,
    clearValue: PropTypes.string.isRequired,
    customUnit: PropTypes.string,
    startOperator: PropTypes.string.isRequired,
    startThreshold: PropTypes.number.isRequired,
    timezone: PropTypes.string.isRequired,
    triggerTimestamp: PropTypes.number.isRequired,
    triggerValue: PropTypes.number.isRequired,
    emailActionCount: PropTypes.number.isRequired,
    pushNotificationActionCount: PropTypes.number.isRequired,
    realSmsActionCount: PropTypes.number.isRequired,
    postActionCount: PropTypes.number.isRequired,
    textActionCount: PropTypes.number.isRequired,
  }).isRequired,
};

export default HistoryListItem;
